import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Section from "../Settings/Section";
import { SeverityMarker } from "../../common/AlertSeverity";
import { useStickyTab } from "../../../utils/uiPrefs";
import { useVisibleLoading } from "../../../utils/useVisibleLoading";
import {
  createAlertTarget,
  deleteAlertTarget,
  getAlertAreas,
  getAlerts,
  updateAlertTarget,
  type AlertAreaWithTargets,
  type AlertTarget,
  type AlertTargetInput,
  type AlertTypeWithTargets,
  type SeverityScope,
} from "../../../api/alerts";

/** Pull the API's `detail` out of an axios error, with a sane fallback. */
function errText(e: any, fallback: string): string {
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

/** "+16155551234" → "(615) 555-1234"; anything else is shown as stored. */
function formatPhone(phone: string | null): string {
  if (!phone) return "—";
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : phone;
}

/** How a recipient is named in confirmations — their name, else their contact. */
function recipientName(t: AlertTarget | null): string {
  if (!t) return "";
  if (t.name) return t.name;
  return t.phone ? formatPhone(t.phone) : t.email ?? "";
}

/**
 * What a recipient subscribed to, as one selectable value.
 *
 * A subscription is either a whole area (optionally high-severity only) or a
 * single type, so the dialog offers one "Applies to" list rather than a mode
 * switch plus two pickers. These strings never leave the component.
 */
const AREA_SCOPE = (area: string, scope: SeverityScope) => `area:${area}:${scope}`;
const TYPE_SCOPE = (key: string) => `type:${key}`;

function scopeOf(t: AlertTarget): string {
  return t.area ? AREA_SCOPE(t.area, t.severity_scope) : TYPE_SCOPE(t.alert_type!);
}

function scopeToInput(scope: string): Pick<
  AlertTargetInput,
  "alert_type" | "area" | "severity_scope"
> {
  const [kind, a, b] = scope.split(":");
  return kind === "area"
    ? { area: a, severity_scope: b as SeverityScope, alert_type: null }
    : { alert_type: a, area: null, severity_scope: "all" };
}

type FormState = {
  scope: string;
  name: string;
  phone: string;
  email: string;
  is_active: boolean;
};

const AlertsConfig: React.FC = () => {
  const [alerts, setAlerts] = useState<AlertTypeWithTargets[]>([]);
  const [areas, setAreas] = useState<AlertAreaWithTargets[]>([]);
  const [loading, setLoading] = useState(true);

  // Add/edit dialog. `editing` null with dialogOpen true = adding.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AlertTarget | null>(null);
  const [form, setForm] = useState<FormState>({
    scope: "",
    name: "",
    phone: "",
    email: "",
    is_active: true,
  });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<AlertTarget | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Which plant area's alerts are on screen. Sticky: configuring one area is
  // usually several visits, and landing back on it saves the hunt.
  const [areaTab, setAreaTab] = useStickyTab("alerts.config.area.v1", 8);

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  // Held briefly so the wheel is visible even when the API answers instantly.
  const showLoading = useVisibleLoading(loading);

  // Clamped on render, so a remembered index still resolves if areas change.
  const activeArea = areas[Math.min(areaTab, Math.max(areas.length - 1, 0))];
  const areaAlerts = useMemo(
    () => alerts.filter((a) => a.area === activeArea?.key),
    [alerts, activeArea]
  );

  /** Human wording for a subscription, used in confirmations. */
  const scopeLabel = useCallback(
    (t: AlertTarget): string => {
      if (t.area) {
        const label = areas.find((a) => a.key === t.area)?.label ?? t.area;
        return t.severity_scope === "high"
          ? `${label} — high priority alerts`
          : `${label} — all alerts`;
      }
      const type = alerts.find((a) => a.key === t.alert_type);
      return type ? `${type.area_label} · ${type.label}` : t.alert_type ?? "";
    },
    [alerts, areas]
  );

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    try {
      const [types, withAreas] = await Promise.all([
        getAlerts(signal),
        getAlertAreas(signal),
      ]);
      setAlerts(types);
      setAreas(withAreas);
    } catch (e: any) {
      if (e?.name !== "CanceledError") {
        showSnack(errText(e, "Failed to load alerts"), "error");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const openAdd = (scope: string) => {
    setEditing(null);
    setForm({ scope, name: "", phone: "", email: "", is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (t: AlertTarget) => {
    setEditing(t);
    setForm({
      scope: scopeOf(t),
      name: t.name ?? "",
      phone: t.phone ?? "",
      email: t.email ?? "",
      is_active: t.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body: AlertTargetInput = {
        ...scopeToInput(form.scope),
        name: form.name.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        is_active: form.is_active,
      };
      if (editing) {
        await updateAlertTarget(editing.id, body);
        showSnack("Recipient updated", "success");
      } else {
        await createAlertTarget(body);
        showSnack("Recipient added", "success");
      }
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      showSnack(errText(e, "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  /** Toggling active is a one-field change, but the API takes the full row. */
  const handleToggleActive = async (t: AlertTarget) => {
    const next = !t.is_active;
    try {
      // PATCH replaces the whole row, so every field has to be sent back —
      // omitting one would blank it out.
      await updateAlertTarget(t.id, {
        alert_type: t.alert_type,
        area: t.area,
        severity_scope: t.severity_scope,
        name: t.name,
        phone: t.phone,
        email: t.email,
        is_active: next,
      });
      const flip = (x: AlertTarget) =>
        x.id === t.id ? { ...x, is_active: next } : x;
      setAlerts((prev) => prev.map((a) => ({ ...a, targets: a.targets.map(flip) })));
      setAreas((prev) => prev.map((a) => ({ ...a, targets: a.targets.map(flip) })));
    } catch (e: any) {
      showSnack(errText(e, "Update failed"), "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    // The delete and the reload after it are two round-trips; without this the
    // dialog just sits there looking unclicked.
    setDeleting(true);
    try {
      await deleteAlertTarget(deleteTarget.id);
      showSnack("Recipient removed", "success");
      setDeleteTarget(null);
      await load();
    } catch (e: any) {
      showSnack(errText(e, "Delete failed"), "error");
    } finally {
      setDeleting(false);
    }
  };

  // Phone and email are independent — either alone is fine, both is fine, but
  // the server (and the DB constraint behind it) rejects neither.
  const hasContact = !!form.phone.trim() || !!form.email.trim();
  const formValid = !!form.scope && hasContact;

  /** A row in a type's list: subscribed to it directly, or via the area. */
  type Row = { t: AlertTarget; inherited: boolean };

  /**
   * Area subscribers who also receive this type — "all" covers everything in
   * the area, "high" only the high-severity types. Listing them here is the
   * honest answer to "who gets this alert": the type card would otherwise say
   * nobody while the area card quietly covers it.
   */
  const rowsForType = useCallback(
    (type: AlertTypeWithTargets): Row[] => {
      const direct = type.targets.map((t) => ({ t, inherited: false }));
      const inherited = (activeArea?.targets ?? [])
        .filter((t) => t.severity_scope === "all" || type.severity === "high")
        .map((t) => ({ t, inherited: true }));
      return [...direct, ...inherited];
    },
    [activeArea]
  );

  /** Recipient table, shared by the area section and each type section. */
  const renderRecipients = (rows: Row[], showScope = false) => (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            {showScope && <TableCell>Receives</TableCell>}
            <TableCell>Phone Number</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Active</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ t, inherited }) => (
            <TableRow key={t.id} hover>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{t.name || "—"}</span>
                  {/* Says where the subscription lives, since it cannot be
                      edited from here. */}
                  {inherited && (
                    <Tooltip
                      title={`Manage in the All ${activeArea?.label} Alerts card above`}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          t.severity_scope === "high"
                            ? "Area · high priority"
                            : "Area · all alerts"
                        }
                        sx={{ height: 20, fontSize: 11 }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </TableCell>
              {showScope && (
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      t.severity_scope === "high" ? "High priority only" : "All alerts"
                    }
                  />
                </TableCell>
              )}
              <TableCell>{formatPhone(t.phone)}</TableCell>
              <TableCell sx={{ wordBreak: "break-all" }}>{t.email || "—"}</TableCell>
              <TableCell>
                <Switch
                  size="small"
                  checked={t.is_active}
                  // Editing an area subscription from one type would silently
                  // change every other type in the area.
                  disabled={inherited}
                  onChange={() => handleToggleActive(t)}
                  inputProps={{ "aria-label": "Recipient active" }}
                />
              </TableCell>
              <TableCell align="right">
                {!inherited && (
                  <>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        aria-label={`Edit: ${recipientName(t)}`}
                        onClick={() => openEdit(t)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Remove: ${recipientName(t)}`}
                        onClick={() => setDeleteTarget(t)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    // Full width of the tab: recipient rows carry name, scope, phone, email,
    // active and actions, and a reading column squeezes them.
    <Box sx={{ width: "100%", py: { xs: 1, sm: 2 } }}>
      {/* The page title lives on the tab shell; this is the tab's own blurb. */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 } }}>
        Each alert type notifies every active recipient listed under it, plus
        anyone subscribed to the whole area.
      </Typography>

      {!showLoading && areas.length > 0 && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: { xs: 1, sm: 2 } }}>
          <Tabs
            value={Math.min(areaTab, areas.length - 1)}
            onChange={(_, v) => setAreaTab(v)}
            aria-label="Alert areas"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {areas.map((area) => (
              <Tab
                key={area.key}
                label={area.label}
                sx={{ minWidth: "auto", px: { xs: 1, md: 2 } }}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {showLoading ? (
        <Box
          aria-busy="true"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <>
          {/* Subscribe once to everything in the area, instead of type by type. */}
          {activeArea && (
            <Section
              sx={{
                bgcolor: "rgba(238, 190, 37, 0.10)",
                borderColor: "rgba(238, 190, 37, 0.55)",
                borderLeft: "4px solid",
                borderLeftColor: "secondary.main",
              }}
              title={`All ${activeArea.label} Alerts`}
              subtitle="Subscribed to the whole area — every alert type below, or only the high priority ones"
              rightAction={
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  onClick={() => openAdd(AREA_SCOPE(activeArea.key, "all"))}
                >
                  Add Recipient
                </Button>
              }
            >
              {activeArea.targets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nobody is subscribed to this area as a whole.
                </Typography>
              ) : (
                renderRecipients(
                  activeArea.targets.map((t) => ({ t, inherited: false })),
                  true
                )
              )}
            </Section>
          )}

          {areaAlerts.map((a) => (
            <Section
              key={a.key}
              title={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <span>{a.label}</span>
                  <SeverityMarker severity={a.severity} />
                </Stack>
              }
              subtitle={a.description || undefined}
              rightAction={
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  size="small"
                  onClick={() => openAdd(TYPE_SCOPE(a.key))}
                >
                  Add Recipient
                </Button>
              }
            >
              {/* The text that goes out when this alert fires. Placeholders are
                  filled in at fire time from the values the alert reports. */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "monospace",
                    bgcolor: "action.hover",
                    borderRadius: 1,
                    px: 1,
                    py: 0.75,
                    mt: 0.25,
                    wordBreak: "break-word",
                  }}
                >
                  {a.template}
                </Typography>
                {a.placeholders.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}
                  >
                    {a.placeholders.map((p) => (
                      <Chip key={p} label={p} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Box>

              {rowsForType(a).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nobody is notified for this alert yet.
                </Typography>
              ) : (
                renderRecipients(rowsForType(a))
              )}
            </Section>
          ))}
        </>
      )}

      {/* Add / edit recipient dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{editing ? "Edit Recipient" : "Add Recipient"}</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            pt: "12px !important",
          }}
        >
          {/* One list covering both kinds of subscription, so a recipient can
              also be moved between them without a separate flow. */}
          <TextField
            select
            label="Applies to"
            fullWidth
            value={form.scope}
            onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))}
            slotProps={{ select: { MenuProps: { sx: { maxHeight: 420 } } } }}
          >
            {areas.flatMap((area) => [
              <ListSubheader key={`h-${area.key}`}>{area.label}</ListSubheader>,
              <MenuItem key={`${area.key}-all`} value={AREA_SCOPE(area.key, "all")}>
                All {area.label} alerts
              </MenuItem>,
              <MenuItem key={`${area.key}-high`} value={AREA_SCOPE(area.key, "high")}>
                {area.label} — high priority only
              </MenuItem>,
              ...alerts
                .filter((a) => a.area === area.key)
                .map((a) => (
                  <MenuItem key={a.key} value={TYPE_SCOPE(a.key)}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ width: "100%" }}
                    >
                      <span>{a.label}</span>
                      <SeverityMarker severity={a.severity} />
                    </Stack>
                  </MenuItem>
                )),
            ])}
          </TextField>
          <TextField
            label="Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Who is this?"
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />
          {/* Stated once, up front, rather than implied by two helper texts. */}
          <Typography variant="caption" color="text.secondary">
            Enter a phone number, an email address, or both — at least one is
            required.
          </Typography>
          <TextField
            label="Phone number"
            fullWidth
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="(615) 555-1234"
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.is_active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, is_active: e.target.checked }))
                }
              />
            }
            label="Active"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formValid}
          >
            {saving ? <CircularProgress size={18} /> : editing ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
      >
        <DialogTitle>Remove Recipient</DialogTitle>
        <DialogContent>
          <Typography>
            Stop notifying <strong>{recipientName(deleteTarget)}</strong> for{" "}
            <strong>{deleteTarget ? scopeLabel(deleteTarget) : ""}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsConfig;
